import { defineStore } from 'pinia'
import {markRaw, ref, shallowRef} from 'vue'
import {useRouter} from "vue-router";

export const useDialogStore = defineStore('dialog', () => {
    const isOpen = ref(false)
    const isConfirmOpen = ref(false)
    const isShowCloseIcon = ref(true)
    const isNeedRightPadding = ref(true)
    const content = shallowRef(null)
    const route = ref(null)
    const router = useRouter()
    const currentRoute = useRoute()
    const checkChanges = ref(() => false)
    const forceConfirm = ref(false)
    const closeBlocked = ref(false)
    const notShowConfirmDialog = ref(false)
    const skipHistoryProp = ref(false)
    const scrollPosition = ref({ left: 0, top: 0 })

    function open(component, props = {}, routeParam) {
        saveScrollPosition()
        isOpen.value = true
        content.value = { component: markRaw(component), props }
        if(routeParam) {
            const params = new URLSearchParams(currentRoute.query)
            const redirect = params.get('redirect')
            params.delete('redirect')
            const stringParams = params.toString()
            const queryWithQuestionMark = stringParams ? `?${stringParams}` : ''
            route.value = `${redirect ? redirect : routeParam}${queryWithQuestionMark}`
        }
        document.documentElement.style.overflow = 'hidden'
        createState()
    }

    function setCheckChanges(onCheckChanges = () => false, alwaysConfirm = false) {
        checkChanges.value = onCheckChanges
        forceConfirm.value = alwaysConfirm
    }

    function setCloseBlocked(blocked) { closeBlocked.value = blocked }

    function close(skipHistory) {
        const targetPath = route.value;
        const shouldUpdateHistory = !skipHistory && !skipHistoryProp.value

        isOpen.value = false
        document.documentElement.style.overflow = ''
        if (shouldUpdateHistory) {
            if (!targetPath) {
                restoreScrollAfterNavigation()
            }
            router.go(-1);
            if (targetPath) {
                setTimeout(() => {
                    router.replace(targetPath)
                }, 50)
            }
        }
        if (!targetPath && !shouldUpdateHistory) {
            restoreScrollPosition()
        }
        route.value = null
        content.value = null
        isNeedRightPadding.value = true
        skipHistoryProp.value = false
        checkChanges.value = () => false
        forceConfirm.value = false
        closeBlocked.value = false
    }

    function restoreScrollAfterNavigation() {
        let restored = false

        const removeAfterEach = router.afterEach(() => {
            restore()
        })
        const fallbackTimer = setTimeout(() => {
            restore()
        }, 300)

        function restore() {
            if (restored) return

            restored = true
            clearTimeout(fallbackTimer)
            removeAfterEach()
            restoreScrollPosition()
        }
    }

    function saveScrollPosition() {
        if (typeof window === 'undefined') return

        scrollPosition.value = {
            left: window.scrollX,
            top: window.scrollY
        }
    }

    function restoreScrollPosition() {
        if (typeof window === 'undefined') return

        const { left, top } = scrollPosition.value

        requestAnimationFrame(() => {
            window.scrollTo(left, top)
            setTimeout(() => window.scrollTo(left, top), 0)
            setTimeout(() => window.scrollTo(left, top), 50)
            setTimeout(() => window.scrollTo(left, top), 150)
        })
    }

    function tryClose(fallCreateState = false) {
        if (isConfirmOpen.value) {
            if (fallCreateState) createState()
            return
        }
        if (closeBlocked.value) {
            if (fallCreateState) createState()
            return
        }
        if (!checkChanges.value() || (notShowConfirmDialog.value && !forceConfirm.value)) {
            close(fallCreateState)
        } else {
            isConfirmOpen.value = true
            if(fallCreateState) {
                createState()
            }
        }
    }

    function createState() {
        router.push({
            query: currentRoute.query,
            hash: '#modal-open',
            state: { isModal: true },
            force: true
        });
    }

    function setRightPadding(need) {
        isNeedRightPadding.value = need
    }

    function cancelConfirm() {
        isConfirmOpen.value = false
    }

    function confirm() {
        isConfirmOpen.value = false
        close()
    }

    function setNotShowConfirmDialog(value) {
        notShowConfirmDialog.value = value
    }

    function setShowCloseIcon(show) {
        isShowCloseIcon.value = show
    }

    function setRoute(r) {
        route.value = r
    }

    function setSkipHistoryProp(v) {
        skipHistoryProp.value = v
    }

    return { isOpen, open, close, tryClose, content, route, setRightPadding, isNeedRightPadding,
        setCheckChanges, isConfirmOpen, cancelConfirm, confirm, notShowConfirmDialog, setNotShowConfirmDialog,
        isShowCloseIcon, setShowCloseIcon, setRoute, setSkipHistoryProp, forceConfirm, setCloseBlocked }
})
